import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { CallCenterStatsService } from './call-center-stats.service';
import { SalesStatsService } from './sales-stats.service';
import { DateService } from './date.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ChangeDetectorRef } from '@angular/core';
import { CallCenterStatsData } from '../interfaces/call-center-stats.interface';
import { SalesStats } from '../interfaces/sales-stats.interface';
import { PerformanceMetric } from '../interfaces/statistics.interface';

@Injectable({
  providedIn: 'root'
})
export class StatisticsDataLoaderService {

  constructor(
    private callCenterStatsService: CallCenterStatsService,
    private salesStatsService: SalesStatsService,
    private dateService: DateService
  ) {}

  /**
   * Adatbázis adatok betöltése a havi táblázathoz
   */
  loadStatsFromDatabase(
    userId: number,
    isKumMode: boolean,
    month: string,
    metrics: PerformanceMetric[],
    daysInMonth: number,
    initializeDefaultMetrics: () => void,
    populateMetricsFromSalesStats: (weeklyStats: SalesStats[]) => void,
    populateMetricsFromWeeklyStats: (weeklyStats: CallCenterStatsData[]) => void,
    snackBar: MatSnackBar,
    cdr: ChangeDetectorRef
  ): void {
    if (!userId || userId === 0) {
      initializeDefaultMetrics();
      cdr.detectChanges();
      return;
    }

    const monthNumber = this.getMonthNumber(month);
    if (monthNumber === -1) {
      return;
    }

    const yearToUse = this.getEffectiveYear();

    if (isKumMode) {
      // KÜM mód: Sales statisztikák használata
      this.salesStatsService.getMonthStats(userId, monthNumber, yearToUse).subscribe({
        next: (weeklyStats: SalesStats[]) => {
          if (weeklyStats.length === 0) {
            initializeDefaultMetrics();
          } else {
            populateMetricsFromSalesStats(weeklyStats);
          }
          
          cdr.detectChanges();
        },
        error: (error) => {
          snackBar.open(
            `Hiba a KÜM statisztikai adatok betöltése során: ${error.message || error.statusText || 'Ismeretlen hiba'}`,
            'Bezárás',
            {
              duration: 6000,
              horizontalPosition: 'center',
              verticalPosition: 'top',
              panelClass: ['error-snackbar']
            }
          );
          
          initializeDefaultMetrics();
          cdr.detectChanges();
        }
      });
    } else {
      // Call Center mód: Call Center statisztikák használata
      this.callCenterStatsService.getMonthStats(userId, monthNumber, yearToUse).subscribe({
        next: (weeklyStats: CallCenterStatsData[]) => {
          if (weeklyStats.length === 0) {
            initializeDefaultMetrics();
          } else {
            populateMetricsFromWeeklyStats(weeklyStats);
          }
          
          cdr.detectChanges();
        },
        error: (error) => {
          snackBar.open(
            `Hiba a statisztikai adatok betöltése során: ${error.message || error.statusText || 'Ismeretlen hiba'}`,
            'Bezárás',
            {
              duration: 6000,
              horizontalPosition: 'center',
              verticalPosition: 'top',
              panelClass: ['error-snackbar']
            }
          );
          
          initializeDefaultMetrics();
          cdr.detectChanges();
        }
      });
    }
  }

  /**
   * Sales heti adatok alapján feltölti a havi táblázat metrikáit
   */
  populateMetricsFromSalesStats(
    weeklyStats: SalesStats[],
    metrics: PerformanceMetric[],
    daysInMonth: number,
    weekNumbers: number[],
    getMonthNumber: () => number,
    updatePercentages: (metricIndex: number) => void,
    initializeDefaultMetrics: () => void
  ): void {
    // Inicializáljuk a metrikákat ha üresek
    if (metrics.length === 0) {
      initializeDefaultMetrics();
    }

    // Megkeressük a metrikákat (KÜM-specifikus)
    const egyeztetésiIdőpontMetricIndex = metrics.findIndex(m => m.name === 'Egyeztetett időpont');
    const vagyonraEgyeztetettMetricIndex = metrics.findIndex(m => m.name === 'Vagyonra egyeztetett');
    const sikeresElsőTárgyalásMetricIndex = metrics.findIndex(m => m.name === 'Sikeres első tárgyalás');
    const sikeresFelmérésMetricIndex = metrics.findIndex(m => m.name === 'Sikeres felmérés');
    const sikeresAjánlatPrezentációMetricIndex = metrics.findIndex(m => m.name === 'Sikeres ajánlat prezentáció');
    const sikeresZárásMetricIndex = metrics.findIndex(m => m.name === 'Sikeres zárás');
    
    // Ellenőrizzük hogy megtaláltuk-e a metrikákat
    const metricsFound = [
      egyeztetésiIdőpontMetricIndex, 
      sikeresElsőTárgyalásMetricIndex, 
      sikeresFelmérésMetricIndex,
      sikeresAjánlatPrezentációMetricIndex,
      sikeresZárásMetricIndex
    ];
    
    if (metricsFound.some(index => index === -1)) {
      return;
    }

    metricsFound.forEach(metricIndex => {
      if (metricIndex !== -1) {
        metrics[metricIndex].actualValues = new Array(daysInMonth).fill(0);
        metrics[metricIndex].planValues = new Array(daysInMonth).fill(0);
      }
    });
    // Vagyonra egyeztetett inicializálása külön (opcionális metrika)
    if (vagyonraEgyeztetettMetricIndex !== -1) {
      metrics[vagyonraEgyeztetettMetricIndex].actualValues = new Array(daysInMonth).fill(0);
      metrics[vagyonraEgyeztetettMetricIndex].planValues = new Array(daysInMonth).fill(0);
    }    
    // Feltöltjük a heti adatokkal
    weeklyStats.forEach(weekData => {
      const weekNumber = weekData.week_number;
      if (!weekNumber) return;
      
      // Megkeressük a hét napjait a hónapban
      weekNumbers.forEach((dayWeekNumber, dayIndex) => {
        if (dayWeekNumber === weekNumber && dayIndex < daysInMonth) {
          const yearToUse = this.getEffectiveYear();
          
          // Meghatározzuk a hét mely napja ez (0=Hétfő, 4=Péntek)
          const date = new Date(yearToUse, getMonthNumber() - 1, dayIndex + 1);
          const dayOfWeek = date.getDay(); // 0=Vasárnap, 1=Hétfő, ..., 6=Szombat
          
          // Konvertálás: 1=Hétfő=0, 2=Kedd=1, ..., 5=Péntek=4
          let weekDayIndex = -1;
          if (dayOfWeek >= 1 && dayOfWeek <= 5) {
            weekDayIndex = dayOfWeek - 1; // 1-5 -> 0-4
          }
          
          // Ha ez egy munkanap és van adatunk róla
          if (weekDayIndex >= 0 && weekDayIndex < 5) {
            
            // Egyeztetett időpont adatok
            if (egyeztetésiIdőpontMetricIndex !== -1 && weekData.appointment_scheduled_actual && weekData.appointment_scheduled_plan) {
              if (weekDayIndex < weekData.appointment_scheduled_actual.length) {
                metrics[egyeztetésiIdőpontMetricIndex].actualValues[dayIndex] = weekData.appointment_scheduled_actual[weekDayIndex];
              }
              if (weekDayIndex < weekData.appointment_scheduled_plan.length) {
                metrics[egyeztetésiIdőpontMetricIndex].planValues[dayIndex] = weekData.appointment_scheduled_plan[weekDayIndex];
              }
            }
            
            // Sikeres első tárgyalás adatok
            if (sikeresElsőTárgyalásMetricIndex !== -1 && weekData.succesfulfirstmeetings_actual && weekData.succesfulfirstmeetings_plan) {
              if (weekDayIndex < weekData.succesfulfirstmeetings_actual.length) {
                metrics[sikeresElsőTárgyalásMetricIndex].actualValues[dayIndex] = weekData.succesfulfirstmeetings_actual[weekDayIndex];
              }
              if (weekDayIndex < weekData.succesfulfirstmeetings_plan.length) {
                metrics[sikeresElsőTárgyalásMetricIndex].planValues[dayIndex] = weekData.succesfulfirstmeetings_plan[weekDayIndex];
              }
            }
            
            // Sikeres felmérés adatok
            if (sikeresFelmérésMetricIndex !== -1 && weekData.successfulassessments_actual && weekData.successfulassessments_plan) {
              if (weekDayIndex < weekData.successfulassessments_actual.length) {
                metrics[sikeresFelmérésMetricIndex].actualValues[dayIndex] = weekData.successfulassessments_actual[weekDayIndex];
              }
              if (weekDayIndex < weekData.successfulassessments_plan.length) {
                metrics[sikeresFelmérésMetricIndex].planValues[dayIndex] = weekData.successfulassessments_plan[weekDayIndex];
              }
            }
            
            // Sikeres ajánlat prezentáció adatok
            if (sikeresAjánlatPrezentációMetricIndex !== -1 && weekData.successfulproposalpresentations_actual && weekData.successfulproposalpresentations_plan) {
              if (weekDayIndex < weekData.successfulproposalpresentations_actual.length) {
                metrics[sikeresAjánlatPrezentációMetricIndex].actualValues[dayIndex] = weekData.successfulproposalpresentations_actual[weekDayIndex];
              }
              if (weekDayIndex < weekData.successfulproposalpresentations_plan.length) {
                metrics[sikeresAjánlatPrezentációMetricIndex].planValues[dayIndex] = weekData.successfulproposalpresentations_plan[weekDayIndex];
              }
            }
            
            // Sikeres zárás adatok
            if (sikeresZárásMetricIndex !== -1 && weekData.successfulclosures_actual && weekData.successfulclosures_plan) {
              if (weekDayIndex < weekData.successfulclosures_actual.length) {
                metrics[sikeresZárásMetricIndex].actualValues[dayIndex] = weekData.successfulclosures_actual[weekDayIndex];
              }
              if (weekDayIndex < weekData.successfulclosures_plan.length) {
                metrics[sikeresZárásMetricIndex].planValues[dayIndex] = weekData.successfulclosures_plan[weekDayIndex];
              }
            }

            // Vagyonra egyeztetett adatok
            if (vagyonraEgyeztetettMetricIndex !== -1 && weekData.property_insurance_appointment_actual && weekData.property_insurance_appointment_plan) {
              if (weekDayIndex < weekData.property_insurance_appointment_actual.length) {
                metrics[vagyonraEgyeztetettMetricIndex].actualValues[dayIndex] = weekData.property_insurance_appointment_actual[weekDayIndex];
              }
              if (weekDayIndex < weekData.property_insurance_appointment_plan.length) {
                metrics[vagyonraEgyeztetettMetricIndex].planValues[dayIndex] = weekData.property_insurance_appointment_plan[weekDayIndex];
              }
            }
          }
        }
      });
    });

    // Újraszámoljuk a százalékokat minden metrikára
    metricsFound.forEach(metricIndex => {
      if (metricIndex !== -1) {
        updatePercentages(metricIndex);
      }
    });
    if (vagyonraEgyeztetettMetricIndex !== -1) {
      updatePercentages(vagyonraEgyeztetettMetricIndex);
    }
  }

  /**
   * Call Center heti adatok alapján feltölti a havi táblázat metrikáit
   */
  populateMetricsFromWeeklyStats(
    weeklyStats: CallCenterStatsData[],
    metrics: PerformanceMetric[],
    daysInMonth: number,
    weekNumbers: number[],
    getMonthNumber: () => number,
    updatePercentages: (metricIndex: number) => void,
    initializeDefaultMetrics: () => void
  ): void {
    // Inicializáljuk a metrikákat ha üresek
    if (metrics.length === 0) {
      initializeDefaultMetrics();
    }

    // Megkeressük a metrikákat
    const indítvásMetricIndex = metrics.findIndex(m => m.name === 'Indított hívás');
    const egyeztetésMetricIndex = metrics.findIndex(m => m.name === 'Egyeztetett időpont');
    const vagyonMetricIndex = metrics.findIndex(m => m.name === 'Vagyonra egyeztetett');
    
    if (indítvásMetricIndex === -1 || egyeztetésMetricIndex === -1) {
      return;
    }

    // Kitöröljük az aktuális értékeket
    metrics[indítvásMetricIndex].actualValues = new Array(daysInMonth).fill(0);
    metrics[indítvásMetricIndex].planValues = new Array(daysInMonth).fill(0);
    metrics[egyeztetésMetricIndex].actualValues = new Array(daysInMonth).fill(0);
    metrics[egyeztetésMetricIndex].planValues = new Array(daysInMonth).fill(0);
    
    if (vagyonMetricIndex !== -1) {
      metrics[vagyonMetricIndex].actualValues = new Array(daysInMonth).fill(0);
      metrics[vagyonMetricIndex].planValues = new Array(daysInMonth).fill(0);
    }
    
    // Feltöltjük a heti adatokkal
    weeklyStats.forEach(weekData => {
      const weekNumber = weekData.week_number;
      
      // Megkeressük a hét napjait a hónapban
      weekNumbers.forEach((dayWeekNumber, dayIndex) => {
        if (dayWeekNumber === weekNumber && dayIndex < daysInMonth) {
          const yearToUse = this.getEffectiveYear();
          
          // Meghatározzuk a hét mely napja ez (0=Hétfő, 4=Péntek)
          const date = new Date(yearToUse, getMonthNumber() - 1, dayIndex + 1);
          const dayOfWeek = date.getDay(); // 0=Vasárnap, 1=Hétfő, ..., 6=Szombat
          
          // Konvertálás: 1=Hétfő=0, 2=Kedd=1, ..., 5=Péntek=4
          let weekDayIndex = -1;
          if (dayOfWeek >= 1 && dayOfWeek <= 5) {
            weekDayIndex = dayOfWeek - 1; // 1-5 -> 0-4
          }
          
          // Ha ez egy munkanap és van adatunk róla
          if (weekDayIndex >= 0 && weekDayIndex < 5) {
            
            // Indított hívás adatok
            if (weekData.initiated_calls_actual && weekData.initiated_calls_plan) {
              if (weekDayIndex < weekData.initiated_calls_actual.length) {
                metrics[indítvásMetricIndex].actualValues[dayIndex] = weekData.initiated_calls_actual[weekDayIndex];
              }
              if (weekDayIndex < weekData.initiated_calls_plan.length) {
                metrics[indítvásMetricIndex].planValues[dayIndex] = weekData.initiated_calls_plan[weekDayIndex];
              }
            }
            
            // Egyeztetett időpont adatok
            if (weekData.appointment_scheduled_actual && weekData.appointment_scheduled_plan) {
              if (weekDayIndex < weekData.appointment_scheduled_actual.length) {
                metrics[egyeztetésMetricIndex].actualValues[dayIndex] = weekData.appointment_scheduled_actual[weekDayIndex];
              }
              if (weekDayIndex < weekData.appointment_scheduled_plan.length) {
                metrics[egyeztetésMetricIndex].planValues[dayIndex] = weekData.appointment_scheduled_plan[weekDayIndex];
              }
            }
            
            // Vagyonra egyeztetett actual értékek
            if (vagyonMetricIndex !== -1 && weekData.property_insurance_appointment_actual) {
              if (weekDayIndex < weekData.property_insurance_appointment_actual.length) {
                metrics[vagyonMetricIndex].actualValues[dayIndex] = weekData.property_insurance_appointment_actual[weekDayIndex];
              }
            }
            
            // Vagyonra egyeztetett plan értékek
            if (vagyonMetricIndex !== -1 && weekData.property_insurance_appointment_plan) {
              if (weekDayIndex < weekData.property_insurance_appointment_plan.length) {
                metrics[vagyonMetricIndex].planValues[dayIndex] = weekData.property_insurance_appointment_plan[weekDayIndex];
              }
            }
          }
        }
      });
    });

    // Újraszámoljuk a százalékokat
    updatePercentages(indítvásMetricIndex);
    updatePercentages(egyeztetésMetricIndex);
    if (vagyonMetricIndex !== -1) updatePercentages(vagyonMetricIndex);
  }

  private getEffectiveYear(): number {
    const currentDate = this.dateService.getCurrentDate();
    const isDebugMode = this.dateService.isDebugMode();
    return isDebugMode ? currentDate.getFullYear() : new Date().getFullYear();
  }

  /**
   * Hónap név alapján szám meghatározása (1-12)
   */
  private getMonthNumber(month: string): number {
    const months = [
      'január', 'február', 'március', 'április', 'május', 'június',
      'július', 'augusztus', 'szeptember', 'október', 'november', 'december'
    ];
    return months.indexOf(month.toLowerCase()) + 1;
  }
}